import { useCallback, useEffect, useRef, useState } from 'react'
import { fetcher } from '../utils/fetcher'
import { useToken } from '../utils/hooks'

type Options<T> = {
  onSuccess?: (data: T) => void
}

export const useFetchOnMount = <T>(
  path: string,
  { onSuccess }: Options<T> = {}
): {
  isError: boolean
  isLoading: boolean
  data: T | undefined
  revalidate: () => Promise<void>
} => {
  const token = useToken()
  const requestMade = useRef(false)
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [data, setData] = useState<T | undefined>(undefined)

  const fetchData = useCallback(
    async (shouldSet: boolean) => {
      const { err, data } = await fetcher<T>({
        path,
        auth: token,
      })
      if (!shouldSet) return
      if (err || !data) {
        setIsError(true)
      } else {
        setIsError(false)
        setData(data)
        onSuccess?.(data)
      }
      setIsLoading(false)
    },
    [path, token, onSuccess]
  )

  useEffect(() => {
    if (requestMade.current) return
    requestMade.current = true

    let shouldSet = true
    // eslint-disable-next-line
    setIsLoading(true)
    fetchData(shouldSet)
    return () => {
      shouldSet = false
    }
  }, [path, token, onSuccess, fetchData])

  return { isError, isLoading, data, revalidate: () => fetchData(true) }
}
